import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import { AlertService } from '@nano/common/alert';
import { ApiService } from '@nano/core/api';
import { CONFIG } from '@nano/core/config';
import { ErrorService } from '@nano/core/error';
import mime from 'mime';
import { NzUploadChangeParam, NzUploadFile, NzUploadXHRArgs, UploadFileStatus } from 'ng-zorro-antd/upload';
import { EMPTY, Observable, Subject, Subscription, of } from 'rxjs';
import { catchError, concatAll, take, takeUntil, tap, finalize } from 'rxjs/operators';
import { FileUploaded } from '../file';
import { FileService } from '../file.service';

const mimeModule = mime as { getType: (value: string) => string | null };

@Component({
  selector: 'app-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class FileUploadComponent implements OnInit, OnChanges, OnDestroy {
  @Output() changed: EventEmitter<NzUploadFile[]> = new EventEmitter<NzUploadFile[]>();
  @Output() uploading: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Input() files: NzUploadFile[] = [];
  @Input() readonly = false;

  readonly action: string;
  readonly mimeTypes: string;

  showUploadList!: { showPreviewIcon?: boolean; showRemoveIcon?: boolean; showDownloadIcon?: boolean };

  private readonly filesMaxCount: number;

  private fileUploadDispatcher: Subject<Observable<unknown>> = new Subject<Observable<unknown>>();
  private fileUploadDispatcherSubscription: Subscription;

  private config = inject(CONFIG);

  constructor(
    private alertService: AlertService,
    private apiService: ApiService,
    private errorService: ErrorService,
    private fileService: FileService
  ) {
    this.action = this.getAction(this.config.oauthEnabled);
    this.mimeTypes = this.config.attachmentTypes.map((type) => mimeModule.getType(type)).join();
    if (this.mimeTypes.includes('application/xml')) {
      this.mimeTypes = `${this.mimeTypes},text/xml`;
    }
    this.filesMaxCount = this.config.attachmentMaxCount;
  }

  ngOnInit(): void {
    this.showUploadList = { showRemoveIcon: !this.readonly, showDownloadIcon: true };

    this.normalizeFiles();

    this.fileUploadDispatcherSubscription = this.fileUploadDispatcher.pipe(concatAll()).subscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.files) {
      this.normalizeFiles();
    }
  }

  ngOnDestroy(): void {
    this.fileUploadDispatcherSubscription?.unsubscribe();
  }

  private normalizeFiles(): void {
    this.files = this.files || [];
    this.files.forEach((file) => {
      file.uid = file.id as string;
      file.status = file.status || 'done';
      if (!file.response) {
        file.response = { status: 'success' };
      }
      file.readonly = true;
    });
  }

  beforeUpload = (fileList: NzUploadFile[]): Observable<boolean> => {
    this.alertService.cleanUp();
    const filesMaxCountExceeded = fileList.length > this.filesMaxCount || this.files?.length + 1 > this.filesMaxCount;
    if (filesMaxCountExceeded) {
      this.alertService.alert({
        type: 'warning',
        icon: 'alert-circle',
        messageType: 'key',
        message: 'fileUpload.maxFilesAllowed',
        messageParams: { maxFilesCount: this.filesMaxCount },
        autoHide: false
      });
    }
    return of(!filesMaxCountExceeded);
  };

  upload = (item: NzUploadXHRArgs): Subscription => {
    return new Observable((observer) => {
      const cancel = new Subject<boolean>();
      const request$ = this.fileService.upload(item).pipe(
        catchError((error: unknown) => {
          item.onError(error, item.file);
          this.errorService.handleRequestError(error);
          return EMPTY;
        }),
        tap(observer),
        takeUntil(cancel),
        finalize(() => {
          const pendingUploads = this.files.filter((file) => file.status === 'uploading');
          if (pendingUploads.length === 0) {
            this.uploading.emit(false);
          }
        })
      );
      this.fileUploadDispatcher.next(request$);
      return () => {
        cancel.next(true);
        cancel.complete();
      };
    }).subscribe((file: FileUploaded) => item.onSuccess(file.id, item.file, file));
  };

  delete = (event: MouseEvent, file: NzUploadFile): void => {
    event.stopPropagation();
    if (!!file.id && !file.readonly) {
      this.fileService
        .delete(file.id as string)
        .pipe(take(1))
        .subscribe();
    }
    this.files.splice(this.files.indexOf(file), 1);
    this.changed.emit(this.filterFiles('done'));
  };

  download = (file: NzUploadFile): void => this.fileService.download(this.getFile(file.uid).id as string, file.name, file.type);

  onChange = (info: NzUploadChangeParam): void => {
    const file: NzUploadFile = info.file;
    let pendingUploads: NzUploadFile[];

    switch (file.status) {
      case 'uploading':
        this.uploading.emit(true);
        break;
      case 'done':
        this.getFile(file.uid).id = file.response as string;
        this.changed.emit(this.filterFiles('done'));
        pendingUploads = this.files.filter((file) => file.status === 'uploading');
        if (pendingUploads.length === 0) {
          this.uploading.emit(false);
        }
        break;
      case 'error':
        this.uploading.emit(false);
        break;
    }
  };

  private getAction = (oauthEnabled: boolean): string => `${oauthEnabled ? '/api' : ''}/ui-api/attachments`;

  private getFile = (uid: string): NzUploadFile => this.files.find((file) => file.uid === uid);

  private filterFiles = (status: UploadFileStatus): NzUploadFile[] => this.files.filter((file) => file.status === status);
}
